import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { BRAND } from "../Brand";

// PERT / Network Diagram — SVG-based node-link chart.
// Nodes are grouped by CPM rank (longest-path level). Critical-path nodes/edges
// are highlighted red. Milestones render as diamonds.

const NODE_W = 176;
const NODE_H = 72;
const H_SPACING = 60;   // horizontal gap between rank columns
const V_SPACING = 28;   // vertical gap between nodes in a column
const PAD = 40;

function fmt(d) {
  return d ? String(d).slice(0, 10) : "—";
}

export default function PertChart({ projectId }) {
  const [data, setData] = useState({ nodes: [], edges: [] });
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(null);
  const [showCritical, setShowCritical] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.get("/plan/pert", { params: { project_id: projectId } })
      .then((r) => setData(r.data || { nodes: [], edges: [] }))
      .catch(() => setData({ nodes: [], edges: [] }))
      .finally(() => setLoading(false));
  }, [projectId]);

  const layout = useMemo(() => {
    if (!data.nodes.length) return { positions: {}, width: 400, height: 200 };
    // group nodes by rank
    const columns = {};
    data.nodes.forEach((n) => {
      const r = n.rank || 0;
      (columns[r] = columns[r] || []).push(n);
    });
    // sort each column by start date then name
    Object.values(columns).forEach((col) => {
      col.sort((a, b) => (a.start || "").localeCompare(b.start || "") || (a.name || "").localeCompare(b.name || ""));
    });
    const maxRank = Math.max(...Object.keys(columns).map((k) => parseInt(k, 10)), 0);
    const positions = {};
    let maxRows = 0;
    for (let r = 0; r <= maxRank; r++) {
      const col = columns[r] || [];
      maxRows = Math.max(maxRows, col.length);
      col.forEach((n, i) => {
        positions[n.id] = {
          x: PAD + r * (NODE_W + H_SPACING),
          y: PAD + i * (NODE_H + V_SPACING),
          rank: r,
        };
      });
    }
    const width = PAD * 2 + (maxRank + 1) * NODE_W + Math.max(0, maxRank) * H_SPACING;
    const height = PAD * 2 + Math.max(1, maxRows) * NODE_H + Math.max(0, maxRows - 1) * V_SPACING;
    return { positions, width, height };
  }, [data]);

  if (loading) {
    return <div className="p-8 text-sm text-neutral-500">Loading network diagram…</div>;
  }
  if (!data.nodes.length) {
    return (
      <div className="p-10 text-center text-sm text-neutral-500">
        No tasks to visualize. Add plan tasks to see the PERT diagram.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-neutral-50" data-testid="plan-pert-chart">
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-neutral-200 px-4 py-2 flex items-center gap-3">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Network Diagram (PERT)</div>
        <div className="text-xs text-neutral-500">
          {data.nodes.length} nodes · {data.edges.length} edges
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            data-testid="pert-critical-toggle"
            onClick={() => setShowCritical((v) => !v)}
            className={`h-7 px-2.5 rounded text-xs uppercase tracking-widest font-semibold border ${
              showCritical ? "bg-red-50 border-red-200 text-red-700" : "border-neutral-200 text-neutral-500"
            }`}
          >
            Critical
          </button>
          <div className="flex items-center gap-1.5 text-[10px] text-neutral-500">
            <div className="h-3 w-3 rounded border-2 border-red-500 bg-red-50" /> Critical
            <div className="h-3 w-3 rounded border-2 border-neutral-300 bg-white ml-2" /> Normal
            <div className="h-3 w-3 rotate-45 bg-orange-400 ml-2" /> Milestone
          </div>
        </div>
      </div>
      <div style={{ minWidth: layout.width, minHeight: layout.height }}>
        <svg width={layout.width} height={layout.height} className="block">
          <defs>
            <marker id="pert-arrow" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#94a3b8" />
            </marker>
            <marker id="pert-arrow-critical" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#ef4444" />
            </marker>
          </defs>

          {/* Edges */}
          {data.edges.map((e, i) => {
            const from = layout.positions[e.from];
            const to = layout.positions[e.to];
            if (!from || !to) return null;
            const fromNode = data.nodes.find((n) => n.id === e.from);
            const toNode = data.nodes.find((n) => n.id === e.to);
            const isCritical = showCritical && fromNode?.is_critical && toNode?.is_critical && e.type === "FS";
            const x1 = from.x + NODE_W;
            const y1 = from.y + NODE_H / 2;
            const x2 = to.x;
            const y2 = to.y + NODE_H / 2;
            const midX = (x1 + x2) / 2;
            const path = `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
            return (
              <g key={`edge-${i}`}>
                <path
                  d={path}
                  fill="none"
                  stroke={isCritical ? "#ef4444" : "#94a3b8"}
                  strokeWidth={isCritical ? 2 : 1.4}
                  markerEnd={isCritical ? "url(#pert-arrow-critical)" : "url(#pert-arrow)"}
                  data-testid={`pert-edge-${e.from}-${e.to}`}
                />
                {(e.type !== "FS" || e.lag_days) ? (
                  <g>
                    <rect x={midX - 20} y={(y1 + y2) / 2 - 8} width="40" height="16" rx="4"
                      fill="white" stroke={isCritical ? "#ef4444" : "#cbd5e1"} strokeWidth="1" />
                    <text x={midX} y={(y1 + y2) / 2 + 3} textAnchor="middle" fontSize="10" fill="#475569" fontFamily="monospace">
                      {e.type}{e.lag_days ? (e.lag_days > 0 ? `+${e.lag_days}` : `${e.lag_days}`) : ""}
                    </text>
                  </g>
                ) : null}
              </g>
            );
          })}

          {/* Nodes */}
          {data.nodes.map((n) => {
            const pos = layout.positions[n.id];
            if (!pos) return null;
            const critical = showCritical && n.is_critical;
            if (n.is_milestone) {
              const cx = pos.x + NODE_W / 2;
              const cy = pos.y + NODE_H / 2;
              const size = 26;
              return (
                <g key={n.id}
                   onMouseEnter={() => setHover(n)}
                   onMouseLeave={() => setHover(null)}
                   data-testid={`pert-node-${n.id}`}
                   style={{ cursor: "pointer" }}>
                  <rect
                    x={cx - size}
                    y={cy - size}
                    width={size * 2}
                    height={size * 2}
                    fill={critical ? "#fee2e2" : "#fed7aa"}
                    stroke={critical ? "#ef4444" : "#f97316"}
                    strokeWidth={2}
                    transform={`rotate(45 ${cx} ${cy})`}
                  />
                  <text x={cx} y={pos.y + NODE_H + 14} textAnchor="middle" fontSize="10" fontFamily="monospace" fill="#334155">
                    {n.wbs} · {n.name?.slice(0, 20)}
                  </text>
                </g>
              );
            }
            return (
              <g key={n.id}
                 onMouseEnter={() => setHover(n)}
                 onMouseLeave={() => setHover(null)}
                 data-testid={`pert-node-${n.id}`}
                 style={{ cursor: "pointer" }}>
                <rect
                  x={pos.x}
                  y={pos.y}
                  width={NODE_W}
                  height={NODE_H}
                  rx="6"
                  fill={critical ? "#fef2f2" : "white"}
                  stroke={critical ? "#ef4444" : "#cbd5e1"}
                  strokeWidth={critical ? 2 : 1.2}
                />
                <rect
                  x={pos.x}
                  y={pos.y}
                  width={NODE_W}
                  height={18}
                  rx="6"
                  fill={critical ? "#ef4444" : BRAND.colors.blue}
                />
                <text x={pos.x + 8} y={pos.y + 12} fontSize="10" fontFamily="monospace" fill="white" fontWeight="bold">
                  {n.wbs}
                </text>
                <text x={pos.x + NODE_W - 8} y={pos.y + 12} textAnchor="end" fontSize="10" fill="white" fontFamily="monospace">
                  Slack {n.slack ?? 0}d
                </text>
                <text x={pos.x + 8} y={pos.y + 34} fontSize="12" fill="#0f172a" fontWeight="600">
                  {(n.name || "").slice(0, 22)}{(n.name || "").length > 22 ? "…" : ""}
                </text>
                <text x={pos.x + 8} y={pos.y + 52} fontSize="10" fill="#64748b" fontFamily="monospace">
                  {fmt(n.start || n.es)} → {fmt(n.finish || n.ef)}
                </text>
                <text x={pos.x + NODE_W - 8} y={pos.y + 52} textAnchor="end" fontSize="10" fill="#64748b" fontFamily="monospace">
                  {n.duration_days ?? 0}d
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      {hover ? (
        <div
          className="fixed bottom-4 right-4 bg-white border border-neutral-200 rounded-lg shadow-lg p-3 text-xs z-20 max-w-xs"
          data-testid="pert-hover-card"
        >
          <div className="font-semibold text-neutral-900">{hover.wbs} · {hover.name}</div>
          <div className="mt-1 space-y-0.5 text-neutral-600 font-mono">
            <div>ES: {fmt(hover.es || hover.start)}  EF: {fmt(hover.ef || hover.finish)}</div>
            <div>LS: {fmt(hover.ls)}  LF: {fmt(hover.lf)}</div>
            <div>Slack: {hover.slack ?? 0}d {hover.is_critical ? <span className="text-red-600 font-bold">· CRITICAL</span> : ""}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
